import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import firebase from './config/firebase';
import {
  COUNTRY_FILTER_FEATURED, COUNTRY_FILTER_ALL,
  SUBMISSION_PUBLISH_STATUSES, COMPARE_MAX,
} from './config/constants';
import {
  getDisplayCountry, formatUsd, formatExactMoney, formatExactShareMoney,
  formatBalanceNav, mergeLiveFxRates, propertyMatchesCountry,
  getCountry, normalizeCountryIso,
  getSupportedMarketTax, estimateInvestmentTotalUsd,
  estimateProRataManagementFeeUsd,
} from './data/countries';
import { PROPERTIES, MARKET_LISTINGS } from './data/demoData';
import {
  googleSignIn,
  setupAuthListener,
  firebaseEmailLogin,
  firebaseEmailRegister,
  userNeedsEmailVerification,
} from './utils/auth';
import apiClient from './utils/api';
import {
  PAGE_PATHS, parseRouteFromLocation, buildUrlForRoute,
  syncRouteToUrl, isAdminUrl,
} from './utils/routing';
import {
  mergeApiWithMockProperties, findPropertyById,
  getPropertyCardImage,
  isPropertyFavorited, isPropertyComparing, normalizeFavoriteIds,
  resolveListingAgentFields, hasListingAgent,
  isFirestoreListingId, fetchFreshPropertyListing,
  scrollPageToTop, runAfterPagePaint,
  normalizePropertyListFunding,
} from './utils/properties';
import {
  getInvestmentCostUsd,
  getSharesOwnedForProperty,
  resolveInvestmentProperty,
} from './utils/investments';
import {
  mergeMarketplaceListingsFromApi,
  computeMarketplaceTradeFees,
} from './utils/marketplace';

import useLocalStorage from './hooks/useLocalStorage';
import useMessageNotifications from './hooks/useMessageNotifications';
import { mergeChatMessages } from './utils/chatMessages';
import {
  unlockMessageNotificationSound,
  requestMessageNotificationPermission
} from './utils/messageNotificationSound';

const primeMessageAlerts = () => {
  unlockMessageNotificationSound();
  requestMessageNotificationPermission();
};

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Toast from './components/layout/Toast';
import ListingChatModal from './components/agent/ListingChatModal';
import CompareTray from './components/property/CompareTray';
import CompareModal from './components/property/CompareModal';
import PropertyDetail from './components/property/PropertyDetail';
import InvestModal from './components/investment/InvestModal';
import SellModal from './components/investment/SellModal';
import MarketplaceBuyModal from './components/investment/MarketplaceBuyModal';
import DepositModal from './components/investment/DepositModal';
import DepositPage from './pages/DepositPage';
import KYCModal from './components/investment/KYCModal';
import HomeAppSection from './components/home/HomeAppSection';

import HomePage from './pages/HomePage';
import ViewListingsPage from './pages/ViewListingsPage';
import FundedPropertiesPage from './pages/FundedPropertiesPage';
import FavoritesPage from './pages/FavoritesPage';
import ProfilePage from './pages/ProfilePage';
import DashboardPage from './pages/DashboardPage';
import MarketplacePage from './pages/MarketplacePage';
import CalculatorPage from './pages/CalculatorPage';
import NewsPage from './pages/NewsPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ListPropertyPage from './pages/ListPropertyPage';
import AgentDashboardPage from './pages/AgentDashboardPage';
import MessagesPage from './pages/MessagesPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import LegalPage from './pages/LegalPage';
import TermsPage from './pages/TermsPage';
import { LEGAL_PAGE_KEYS } from './config/legalPages';

const App = () => {
  // --- App state: current page, selected property, user, wallet, listings ---
  const initialRoute = useMemo(() => parseRouteFromLocation(), []);
  const [page, setPageInternal] = useState(
    initialRoute.page && PAGE_PATHS[initialRoute.page] ? initialRoute.page : "home"
  );
  const pageBeforeDepositRef = useRef(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [selectedMarketListing, setSelectedMarketListing] = useState(null);
  const [buyingMarket, setBuyingMarket] = useState(false);
  const [pendingPropertyId, setPendingPropertyId] = useState(initialRoute.propertyId);
  const routeHydratedRef = useRef(false);
  const [country, setCountry] = useLocalStorage("coown_country", COUNTRY_FILTER_FEATURED);
  const [balance, setBalance] = useLocalStorage("coown_balance", 0);
  const [investments, setInvestments] = useLocalStorage("coown_investments", []);
  const [favorites, setFavorites] = useLocalStorage("coown_favorites", []);
  const [compareList, setCompareList] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [marketListings, setMarketListings] = useState(MARKET_LISTINGS);
  const [agentSubmissions, setAgentSubmissions] = useState([]);
  const [modal, setModal] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [kycDone, setKycDone] = useLocalStorage("coown_kyc", false);
  const [showKYC, setShowKYC] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useLocalStorage("coown_logged_in", false);
  const [user, setUser] = useLocalStorage("coown_user", null);
  const [serverRole, setServerRole] = useState(null);
  const [adminCheckPending, setAdminCheckPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [properties, setProperties] = useState(PROPERTIES);
  const [catalogProperties, setCatalogProperties] = useState(PROPERTIES);
  const [depositing, setDepositing] = useState(false);
  const [testDepositsEnabled, setTestDepositsEnabled] = useState(false);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  // --- URL routing: sync address bar with page + /property/:id ---
  const applyRoute = (route, { fromPopState = false } = {}) => {
    const nextPage = route.page && PAGE_PATHS[route.page] ? route.page : "home";
    setPageInternal(nextPage);
    if (route.propertyId) {
      setPendingPropertyId(route.propertyId);
    } else {
      setPendingPropertyId(null);
      setSelectedProperty(null);
    }
    if (!fromPopState && nextPage !== "admin") {
      try { localStorage.setItem("coown_page", JSON.stringify(nextPage)); } catch (_) {}
    }
    scrollPageToTop();
  };

  const setPage = (p, { replace = false, keepProperty = false, scrollAfter } = {}) => {
    const nextPage = p && PAGE_PATHS[p] ? p : "home";
    const propertyId = keepProperty && selectedProperty ? selectedProperty.id : null;
    setPageInternal(nextPage);
    if (!keepProperty) {
      setSelectedProperty(null);
      setPendingPropertyId(null);
    }
    syncRouteToUrl(nextPage, propertyId, { replace });
    if (nextPage !== "admin") {
      try { localStorage.setItem("coown_page", JSON.stringify(nextPage)); } catch (_) {}
    }
    scrollPageToTop();
    if (scrollAfter) runAfterPagePaint(scrollAfter);
  };

  const selectProperty = (property) => {
    if (!property) return;
    setSelectedProperty(property);
    setPendingPropertyId(null);
    scrollPageToTop();
    syncRouteToUrl(page, property.id, { replace: false });
    fetchFreshPropertyListing(property).then((fresh) => {
      if (fresh?.visibleOnSite === false) {
        addToast("This listing is no longer on the public site.", "info");
        setSelectedProperty(null);
        syncRouteToUrl(page, null, { replace: true });
        return;
      }
      if (fresh?.id) setSelectedProperty(fresh);
    });
  };

  const openPropertyListing = (propertyOrId) => {
    const property =
      propertyOrId && typeof propertyOrId === "object" && propertyOrId.id
        ? propertyOrId
        : findPropertyById(catalogProperties, propertyOrId)
          || findPropertyById(properties, propertyOrId)
          || (() => {
            const inv = investments.find((i) => String(i.propertyId) === String(propertyOrId));
            return inv ? resolveInvestmentProperty(catalogProperties, inv) || resolveInvestmentProperty(properties, inv) : null;
          })();
    if (!property?.id) {
      addToast("Could not open this listing. Try browsing from Home.", "info");
      return;
    }
    setPageInternal("home");
    setSelectedProperty(property);
    setPendingPropertyId(null);
    syncRouteToUrl("home", property.id, { replace: false });
    try { localStorage.setItem("coown_page", JSON.stringify("home")); } catch (_) {}
    fetchFreshPropertyListing(property).then((fresh) => {
      if (fresh?.visibleOnSite === false) {
        addToast("This listing is no longer on the public site.", "info");
        setSelectedProperty(null);
        syncRouteToUrl("home", null, { replace: true });
        return;
      }
      if (fresh?.id) setSelectedProperty(fresh);
    });
  };

  const clearSelectedProperty = () => {
    setSelectedProperty(null);
    setPendingPropertyId(null);
    syncRouteToUrl(page, null, { replace: true });
    scrollPageToTop();
  };

  const navigateToPage = (p, opts = {}) => {
    setPage(p, opts);
  };

  const returnFromDeposit = () => {
    const previous = pageBeforeDepositRef.current;
    pageBeforeDepositRef.current = null;
    setPage(previous && previous !== "deposit" ? previous : "dashboard");
  };

  const openDepositPage = () => {
    pageBeforeDepositRef.current = page;
    navigateToPage("deposit");
  };

  const goToBrowseListings = ({ listingMode = "all", search = "", goToRent = false } = {}) => {
    try {
      if (search) sessionStorage.setItem("coown_browse_search", search);
      else sessionStorage.removeItem("coown_browse_search");
      sessionStorage.removeItem("coown_browse_rent_results_only");
      sessionStorage.setItem("coown_browse_listing_mode", listingMode);
    } catch (_) {}
    navigateToPage(goToRent ? "rent" : "view-listings");
  };

  // --- On first load: fix browser URL to match route ---
  useEffect(() => {
    if (routeHydratedRef.current) return;
    routeHydratedRef.current = true;
    try {
      if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    } catch (_) {}
    const route = parseRouteFromLocation();
    const path = buildUrlForRoute(route.page, route.propertyId);
    const url = new URL(window.location.href);
    url.pathname = path;
    url.searchParams.delete("page");
    const next = url.pathname + url.search + url.hash;
    window.history.replaceState(
      { page: route.page, propertyId: route.propertyId ? String(route.propertyId) : null },
      "",
      next
    );
  }, []);

  useEffect(() => {
    if (!pendingPropertyId) return;
    const found =
      findPropertyById(catalogProperties, pendingPropertyId) ||
      findPropertyById(properties, pendingPropertyId) ||
      findPropertyById(PROPERTIES, pendingPropertyId);
    if (found) {
      setSelectedProperty(found);
      setPendingPropertyId(null);
      if (isFirestoreListingId(found.id)) {
        fetchFreshPropertyListing(found).then((fresh) => {
          if (fresh?.id) setSelectedProperty(fresh);
        });
      }
    }
  }, [pendingPropertyId, catalogProperties, properties]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    const fresh =
      findPropertyById(catalogProperties, selectedProperty.id) ||
      findPropertyById(properties, selectedProperty.id);
    if (!fresh) return;
    const sharesChanged = Number(fresh.shares) !== Number(selectedProperty.shares);
    const minChanged = Number(fresh.minInvestment) !== Number(selectedProperty.minInvestment);
    const priceChanged = Number(fresh.price) !== Number(selectedProperty.price);
    const soldChanged = Number(fresh.sharesSold) !== Number(selectedProperty.sharesSold);
    if (sharesChanged || minChanged || priceChanged || soldChanged) {
      setSelectedProperty(fresh);
    }
  }, [catalogProperties, properties, selectedProperty?.id]);

  useEffect(() => {
    const onPopState = (event) => {
      const route = event.state && event.state.page
        ? { page: event.state.page, propertyId: event.state.propertyId || null }
        : parseRouteFromLocation();
      applyRoute(route, { fromPopState: true });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // One-time: reset legacy demo default balance ($50,000) to $0.00
  useEffect(() => {
    try {
      if (localStorage.getItem("coown_balance_legacy_reset")) return;
      if (localStorage.getItem("coown_balance") === "50000") {
        setBalance(0);
        localStorage.setItem("coown_balance_legacy_reset", "1");
      }
    } catch (_) {}
  }, []);

  // --- Load listings from API (plus demo data for selected country) ---
  const loadProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiFilters = (country === COUNTRY_FILTER_FEATURED || country === COUNTRY_FILTER_ALL)
        ? {}
        : { country };
      const res = await apiClient.getProperties(apiFilters);
      const mockForCountry = PROPERTIES.filter(p => propertyMatchesCountry(p.country, country));
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        const apiFiltered = res.data.filter(p => propertyMatchesCountry(p.country, country));
        setProperties(normalizePropertyListFunding(mergeApiWithMockProperties(apiFiltered, mockForCountry)));
      } else {
        setProperties(mockForCountry);
      }
    } catch (err) {
      console.error('Failed to load properties:', err);
      setError(err.message);
      setProperties(PROPERTIES.filter(p => propertyMatchesCountry(p.country, country)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProperties(); }, [country]);

  const loadPropertyCatalog = async () => {
    try {
      const res = await apiClient.getProperties({});
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        setCatalogProperties(normalizePropertyListFunding(mergeApiWithMockProperties(res.data, PROPERTIES)));
        return;
      }
    } catch (_) { /* use mock */ }
    setCatalogProperties(PROPERTIES);
  };

  const refreshInvestments = async () => {
    if (!firebase.auth().currentUser) return;
    try {
      const res = await apiClient.getInvestments();
      if (res.data && Array.isArray(res.data)) {
        const normalized = res.data.map((inv) => ({
          ...inv,
          invested: getInvestmentCostUsd(inv),
          shares: Number(inv.shares) || 0,
          purchaseDate: inv.purchaseDate?.toDate
            ? inv.purchaseDate.toDate().toISOString().split("T")[0]
            : inv.purchaseDate,
        }));
        setInvestments(normalized);
      }
    } catch (err) {
      console.error("Failed to refresh investments:", err);
    }
  };

  const loadMarketplaceListings = async () => {
    try {
      const res = await apiClient.getMarketplaceListings();
      const apiRows = Array.isArray(res.data) ? res.data : [];
      setMarketListings(mergeMarketplaceListingsFromApi(apiRows, MARKET_LISTINGS));
    } catch (err) {
      console.warn("Marketplace listings unavailable:", err.message);
      setMarketListings(MARKET_LISTINGS);
    }
  };

  useEffect(() => { loadPropertyCatalog(); loadMarketplaceListings(); }, []);
  useEffect(() => { loadFxRates(); }, []);
  useEffect(() => {
    if (page === "marketplace") loadMarketplaceListings();
  }, [page]);

  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    const currentUser = firebase.auth().currentUser;
    if (currentUser && userNeedsEmailVerification(currentUser) && serverRole !== "admin") return;
    apiClient.getWallet().then((res) => {
      if (res.data) {
        if (typeof res.data.testDepositsEnabled === "boolean") {
          setTestDepositsEnabled(res.data.testDepositsEnabled);
        }
        if (Array.isArray(res.data.transactions)) {
          setWalletTransactions(res.data.transactions);
        }
        if (res.data.stripePublishableKey) {
          setStripePublishableKey(res.data.stripePublishableKey);
        }
      }
    }).catch(() => {});
  }, [authReady, isLoggedIn, serverRole]);

  // --- Who is logged in? Role (admin vs investor) comes from server ---
  const refreshUserProfile = async () => {
    if (!firebase.auth().currentUser) {
      setServerRole(null);
      return null;
    }
    setAdminCheckPending(true);
    try {
      const res = await apiClient.getMe();
      if (res.data) {
        const role = res.data.role || "investor";
        setServerRole(role);
        setUser({ ...res.data, role });
        if (typeof res.data.balance === "number") setBalance(res.data.balance);
        if (res.data.kycStatus === "approved") setKycDone(true);
        else if (res.data.kycStatus) setKycDone(false);
        return res.data;
      }
    } catch (err) {
      console.error("Failed to refresh user profile:", err);
      if (String(err.message || "").includes("suspended")) {
        try {
          await firebase.auth().signOut();
        } catch (_) {}
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsLoggedIn(false);
        setUser(null);
        setServerRole(null);
        showAuthToast(err.message, "error");
        setPage("login");
        return null;
      }
      setServerRole("investor");
      if (user) setUser(prev => prev ? { ...prev, role: "investor" } : prev);
    } finally {
      setAdminCheckPending(false);
    }
    return null;
  };

  /** Admin is only true when the server (Firestore) confirms role — never from URL or cached guess alone. */
  const isAdmin = authReady && isLoggedIn && serverRole === "admin";

  useEffect(() => {
    if (page === "admin" && isLoggedIn && authReady) refreshUserProfile();
  }, [page, isLoggedIn, authReady]);

  // --- Firebase auth listener: signed in vs signed out ---
  // Sync login state with Firebase before calling protected APIs
  useEffect(() => {
    const unsubscribe = setupAuthListener((firebaseUser) => {
      setAuthReady(true);
      if (firebaseUser) {
        console.log('[APP] Auth state: user signed in');
        setIsLoggedIn(true);
      } else if (!window.__coownSigningIn) {
        console.log('[APP] Auth state: user signed out');
        setIsLoggedIn(false);
        setUser(null);
        setServerRole(null);
        setInvestments([]);
        setFavorites([]);
        setWalletTransactions([]);
        setBalance(0);
      }
    });
    return () => unsubscribe();
  }, []);

  // Only load investments/favorites when Firebase has an active session
  useEffect(() => {
    if (!authReady || !isLoggedIn || !firebase.auth().currentUser) return;
    if (userNeedsEmailVerification(firebase.auth().currentUser) && serverRole !== "admin") return;

    refreshUserProfile();
    syncKycStatus();

    const loadInvestments = async () => {
      try {
        const res = await apiClient.getInvestments();
        if (res.data && Array.isArray(res.data)) {
          const normalized = res.data.map((inv) => ({
            ...inv,
            invested: getInvestmentCostUsd(inv),
            shares: Number(inv.shares) || 0,
            purchaseDate: inv.purchaseDate?.toDate
              ? inv.purchaseDate.toDate().toISOString().split("T")[0]
              : inv.purchaseDate,
          }));
          setInvestments(normalized);
        }
        loadPropertyCatalog();
      } catch (err) {
        if (!err.message?.includes('Not authorized')) {
          console.error('Failed to load investments:', err);
        }
      }
    };

    const loadFavorites = async () => {
      let localIds = [];
      try {
        localIds = normalizeFavoriteIds(JSON.parse(localStorage.getItem("coown_favorites") || "[]"));
      } catch (_) { /* ignore */ }

      try {
        const res = await apiClient.getFavorites();
        if (res.data && Array.isArray(res.data)) {
          const apiIds = res.data.map((f) => f.propertyId);
          const merged = normalizeFavoriteIds([...apiIds, ...localIds]);
          setFavorites(merged);
          for (const id of localIds) {
            if (!apiIds.some((aid) => String(aid) === String(id))) {
              try { await apiClient.addFavorite(String(id)); } catch (_) { /* demo ids may fail */ }
            }
          }
          return;
        }
      } catch (err) {
        if (!err.message?.includes("Not authorized")) {
          console.error("Failed to load favorites:", err);
        }
      }
      setFavorites(localIds);
    };

    const reloadInvestmentsFromApi = async () => {
      try {
        const invRes = await apiClient.getInvestments();
        if (invRes.data && Array.isArray(invRes.data)) {
          const normalized = invRes.data.map((inv) => ({
            ...inv,
            invested: getInvestmentCostUsd(inv),
            shares: Number(inv.shares) || 0,
            purchaseDate: inv.purchaseDate?.toDate
              ? inv.purchaseDate.toDate().toISOString().split('T')[0]
              : inv.purchaseDate
          }));
          setInvestments(normalized);
        }
      } catch (err) {
        if (!err.message?.includes('Not authorized')) {
          console.error('Failed to reload investments after recovery:', err);
        }
      }
    };

    const recoverStuckCryptoCheckouts = async () => {
      try {
        const res = await apiClient.recoverPendingCryptoCheckouts();
        const recovered = res.data?.recovered || [];
        const alreadySettled = res.data?.alreadySettled || [];
        const pending = res.data?.pending || [];

        if (recovered.length > 0) {
          addToast(
            `Recovered ${recovered.length} crypto payment${recovered.length > 1 ? 's' : ''} — your investment${recovered.length > 1 ? 's are' : ' is'} now in your portfolio.`,
            'success'
          );
          await reloadInvestmentsFromApi();
          loadPropertyCatalog();
        } else if (alreadySettled.length > 0) {
          // Payments were already credited — silently clean up stale checkout sessions.
          await reloadInvestmentsFromApi();
        } else if (pending.length > 0) {
          console.warn('Crypto checkout recovery pending:', pending);
        }
      } catch (err) {
        if (!err.message?.includes('Not authorized')) {
          console.warn('Crypto checkout recovery skipped:', err.message);
        }
      }
    };

    loadInvestments();
    loadFavorites();
    recoverStuckCryptoCheckouts();
  }, [authReady, isLoggedIn, serverRole]);

  const addToast = (msg, type="success") => {
    const id = Date.now();
    setToasts(prev => [...prev, {id,msg,type}]);
  };

  const [listingChat, setListingChat] = useState(null);
  const [activeInboxConversationId, setActiveInboxConversationId] = useState(null);

  const { unreadCount: messagesUnreadCount, refreshUnreadCount } = useMessageNotifications({
    enabled: authReady && isLoggedIn,
    addToast,
    suppressAlerts: Boolean(listingChat) || Boolean(activeInboxConversationId),
  });

  const syncMessagesUnreadCount = useCallback(
    () => refreshUnreadCount({ silent: true }),
    [refreshUnreadCount]
  );

  const notifyNewMessages = useCallback(
    () => refreshUnreadCount({ silent: false }),
    [refreshUnreadCount]
  );

  const handleActiveInboxConversationChange = useCallback((conversationId) => {
    setActiveInboxConversationId(conversationId ? String(conversationId) : null);
  }, []);
  const [listingChatSending, setListingChatSending] = useState(false);
  const pendingListingChatPropertyIdRef = useRef(null);
  const listingChatPollSeqRef = useRef(0);

  const closeListingChat = () => setListingChat(null);

  const openListingChatForProperty = async (property, options = {}) => {
    const resolved = resolveListingAgentFields(property);
    const chatOptions = options && typeof options === "object" ? options : {};
    const initialDraft = String(chatOptions.draft || "").trim();
    const intent = chatOptions.intent === "consultation" ? "consultation" : "message";
    if (!resolved?.id) return;
    if (!String(resolved.agentEmail || "").trim()) {
      addToast("This listing does not have an agent email yet. The lister may need to republish with contact details.", "error");
      return;
    }
    if (!isLoggedIn || !firebase.auth().currentUser) {
      pendingListingChatPropertyIdRef.current = String(resolved.id);
      try {
        sessionStorage.setItem("coown_pending_listing_chat", String(resolved.id));
        sessionStorage.setItem("coown_pending_listing_chat_page", page);
        if (initialDraft) sessionStorage.setItem("coown_pending_listing_chat_draft", initialDraft);
        sessionStorage.setItem("coown_pending_listing_chat_intent", intent);
      } catch (_) {}
      addToast("Sign in to message the listing agent on CoOwn.", "info");
      setPage("login", { keepProperty: true });
      return;
    }
    try {
      const previewRes = await apiClient.getPropertyListingChat(resolved.id);
      const preview = previewRes.data || {};
      const existingConv = preview.conversation;
      const conv = existingConv || {
        propertyId: String(resolved.id),
        propertyTitle: resolved.title || preview.property?.propertyTitle || "Listing",
        propertyLocation: resolved.location || preview.property?.propertyLocation || "",
        listingMode: resolved.listingMode === "rent" ? "rent" : "sale",
        agentName: resolved.agentName || preview.property?.agentName || "Listing agent",
        agentEmail: resolved.agentEmail || preview.property?.agentEmail || "",
        agentCompany: resolved.agentCompany || preview.property?.agentCompany || "",
        agentPhone: resolved.agentPhone || preview.property?.agentPhone || ""
      };
      setListingChat({
        propertyId: String(resolved.id),
        conversation: conv,
        messages: preview.messages || [],
        initialDraft,
        intent
      });
      syncMessagesUnreadCount();
    } catch (err) {
      addToast(err.message || "Could not open chat with the listing agent.", "error");
    }
  };

  const sendListingChatMessage = async (text) => {
    const convId = listingChat?.conversation?.id;
    const propertyId = listingChat?.propertyId || listingChat?.conversation?.propertyId;
    if (!convId && !propertyId) return;
    setListingChatSending(true);
    listingChatPollSeqRef.current += 1;
    try {
      if (convId) {
        const res = await apiClient.sendListingMessage(convId, text);
        const newMsg = res.data;
        setListingChat((prev) => ({
          ...prev,
          messages: mergeChatMessages(prev?.messages || [], [newMsg])
        }));
      } else {
        const res = await apiClient.sendPropertyListingMessage(propertyId, text);
        const { conversation, message } = res.data || {};
        setListingChat((prev) => ({
          ...prev,
          propertyId: String(propertyId),
          conversation: conversation || prev?.conversation,
          messages: mergeChatMessages(prev?.messages || [], message ? [message] : [])
        }));
      }
      syncMessagesUnreadCount();
    } catch (err) {
      addToast(err.message || "Could not send your message.", "error");
    } finally {
      setListingChatSending(false);
    }
  };

  useEffect(() => {
    const convId = listingChat?.conversation?.id;
    if (!convId || !isLoggedIn) return undefined;

    const pollOpenChat = async () => {
      const seq = ++listingChatPollSeqRef.current;
      try {
        const res = await apiClient.getListingMessages(convId, { markRead: true });
        if (seq !== listingChatPollSeqRef.current) return;
        const incoming = res.data?.messages || [];
        setListingChat((prev) => {
          if (!prev?.conversation || prev.conversation.id !== convId) return prev;
          const merged = mergeChatMessages(prev.messages || [], incoming);
          if (merged.length === (prev.messages || []).length) return prev;
          return {
            ...prev,
            conversation: res.data?.conversation || prev.conversation,
            messages: merged
          };
        });
        syncMessagesUnreadCount();
      } catch (_) { /* ignore poll errors */ }
    };

    const id = window.setInterval(pollOpenChat, 15000);
    return () => window.clearInterval(id);
  }, [listingChat?.conversation?.id, isLoggedIn, syncMessagesUnreadCount]);

  const restoreListingChatContext = () => {
    let propertyId = pendingListingChatPropertyIdRef.current;
    let returnPage = null;
    let pendingDraft = "";
    let pendingIntent = "message";
    try {
      if (!propertyId) propertyId = sessionStorage.getItem("coown_pending_listing_chat");
      returnPage = sessionStorage.getItem("coown_pending_listing_chat_page");
      pendingDraft = sessionStorage.getItem("coown_pending_listing_chat_draft") || "";
      pendingIntent = sessionStorage.getItem("coown_pending_listing_chat_intent") || "message";
    } catch (_) {}
    if (!propertyId) return false;

    const prop =
      findPropertyById(catalogProperties, propertyId) ||
      findPropertyById(properties, propertyId) ||
      selectedProperty;
    const targetPage = returnPage && PAGE_PATHS[returnPage] ? returnPage : page;

    if (prop?.id) {
      setPageInternal(targetPage);
      setSelectedProperty(prop);
      setPendingPropertyId(null);
      syncRouteToUrl(targetPage, prop.id, { replace: true });
      scrollPageToTop();
    }

    pendingListingChatPropertyIdRef.current = null;
    try {
      sessionStorage.removeItem("coown_pending_listing_chat");
      sessionStorage.removeItem("coown_pending_listing_chat_page");
      sessionStorage.removeItem("coown_pending_listing_chat_draft");
      sessionStorage.removeItem("coown_pending_listing_chat_intent");
    } catch (_) {}

    if (prop?.id) {
      fetchFreshPropertyListing(prop).then((fresh) => {
        if (fresh?.id) setSelectedProperty(fresh);
      });
      openListingChatForProperty(prop, { draft: pendingDraft, intent: pendingIntent });
    }
    return !!prop?.id;
  };

  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    if (restoreListingChatContext()) return;
    if (page === "login") {
      if (isAdminUrl()) setPage("admin");
      else setPage("home");
    }
  }, [authReady, isLoggedIn]);

  const showAuthToast = (msg, type = "success") => {
    setToasts([]);
    addToast(msg, type);
  };

  const toggleFav = async (id) => {
    const idStr = String(id);
    const wasFav = isPropertyFavorited(favorites, id);

    const applyLocal = (nextFav) => {
      setFavorites(nextFav);
      try { localStorage.setItem("coown_favorites", JSON.stringify(nextFav)); } catch (_) {}
    };

    if (!isLoggedIn) {
      addToast("Sign in to save favorites to your account.", "info");
      setPage("login");
      return;
    }

    try {
      if (wasFav) {
        await apiClient.removeFavorite(idStr);
        applyLocal(favorites.filter((x) => String(x) !== idStr));
        addToast("Removed from favorites", "info");
      } else {
        await apiClient.addFavorite(idStr);
        applyLocal(normalizeFavoriteIds([...favorites, idStr]));
        addToast("Saved to favorites", "success");
      }
    } catch (error) {
      console.error("Favorite toggle failed:", error);
      const next = wasFav
        ? favorites.filter((x) => String(x) !== idStr)
        : normalizeFavoriteIds([...favorites, idStr]);
      applyLocal(next);
      addToast(wasFav ? "Removed from favorites (offline)" : "Saved locally — sync when online", "info");
    }
  };

  const toggleCompare = (prop) => {
    if (!prop) return;
    const idStr = String(prop.id);
    if (isPropertyComparing(compareList, idStr)) {
      setCompareList((prev) => prev.filter((p) => String(p.id) !== idStr));
      return;
    }
    if (compareList.length >= COMPARE_MAX) {
      addToast(`You can compare up to ${COMPARE_MAX} listings at a time. Remove one to add another.`, "info");
      return;
    }
    setCompareList((prev) => [...prev, prop]);
  };

  const removeFromCompare = (id) => {
    const idStr = String(id);
    setCompareList((prev) => prev.filter((p) => String(p.id) !== idStr));
  };

  const clearCompare = () => setCompareList([]);

  useEffect(() => {
    if (compareList.length > 0) {
      document.body.classList.add("has-compare-tray");
    } else {
      document.body.classList.remove("has-compare-tray");
    }
    return () => document.body.classList.remove("has-compare-tray");
  }, [compareList.length]);

  const invest = async (property) => {
    if (!kycDone) { setShowKYC(true); return; }
    if (!isLoggedIn) { setPage("login"); return; }
    setSelectedProperty(property);
    setModal("invest");
  };

  // --- User actions: invest, sell, deposit, favorites, compare ---
  const confirmInvest = async (shares, totalUsd, settlementRail = 'fiat', payAsset = 'USDCa') => {
    try {
      if (settlementRail === 'algorand') {
        const quoteRes = await apiClient.createCryptoCheckoutQuote(selectedProperty.id, shares, payAsset);
        const sessionId = quoteRes.data?.sessionId;
        if (!sessionId) throw new Error('Could not create crypto checkout quote');
        const payLabel =
          payAsset === 'ALGO'
            ? `${quoteRes.data.payAmountDisplay} ALGO → ${formatUsd(totalUsd)} USDC`
            : `${formatUsd(totalUsd)} USDC`;
        addToast(`Paying ${payLabel}…`, 'info');
        let execRes;
        try {
          execRes = await apiClient.executeCryptoCheckout(sessionId);
        } catch (firstErr) {
          if (String(firstErr.message || '').includes('payment was received')) {
            execRes = await apiClient.executeCryptoCheckout(sessionId);
          } else {
            throw firstErr;
          }
        }
        const investment = execRes.data?.investment;
        const charged = investment?.invested ?? totalUsd;
        if (typeof investment?.balance === 'number') setBalance(investment.balance);
        setInvestments((prev) => [...prev, investment]);
        addToast(`Invested ${formatUsd(charged)} via ${payAsset} (settled as USDC).`);
        loadPropertyCatalog();
      } else {
        if (totalUsd > balance + 0.001) { addToast("Insufficient USD balance (incl. VAT).", "error"); return; }
        const res = await apiClient.invest(selectedProperty.id, shares, settlementRail);
        const charged = res.data?.invested ?? res.data?.totalUsd ?? totalUsd;
        if (typeof res.data?.balance === "number") setBalance(res.data.balance);
        else setBalance((b) => Math.round((b - charged) * 100) / 100);
        setInvestments((prev) => [...prev, res.data]);
        addToast(`Invested ${formatUsd(charged)} in ${selectedProperty.title}!`);
        loadPropertyCatalog();
      }
    } catch (error) {
      addToast(error.message, "error");
    }
    setModal(null);
  };

  const sell = (investmentOrGroup) => {
    const propertyId = investmentOrGroup?.propertyId;
    const p =
      properties.find((prop) => String(prop.id) === String(propertyId)) ||
      catalogProperties.find((prop) => String(prop.id) === String(propertyId)) ||
      investmentOrGroup?.lots?.[0]?.property;
    setSelectedInvestment(investmentOrGroup);
    setSelectedProperty(p ? { id: propertyId, ...p } : null);
    setModal("sell");
  };

  const patchPropertyFundingState = (propertyId, sharesSold, status) => {
    if (propertyId == null || sharesSold == null) return;
    const apply = (list) =>
      (Array.isArray(list) ? list : []).map((p) => {
        if (String(p.id) !== String(propertyId)) return p;
        const total = p.shares || 0;
        return {
          ...p,
          sharesSold,
          status: status || p.status,
          fundingProgress: total ? (sharesSold / total) * 100 : 0
        };
      });
    setProperties((prev) => apply(prev));
    setCatalogProperties((prev) => apply(prev));
    if (selectedProperty && String(selectedProperty.id) === String(propertyId)) {
      setSelectedProperty((p) => ({
        ...p,
        sharesSold,
        status: status || p.status,
        fundingProgress: p.shares ? (sharesSold / p.shares) * 100 : 0
      }));
    }
  };

  const confirmSell = async (shares) => {
    const lots =
      Array.isArray(selectedInvestment?.lots) && selectedInvestment.lots.length
        ? selectedInvestment.lots.filter((lot) => lot?.id)
        : selectedInvestment?.id
          ? [selectedInvestment]
          : [];

    if (!lots.length) {
      addToast("Investment not found. Refresh and try again.", "error");
      setModal(null);
      return;
    }

    try {
      let remaining = shares;
      let lastData = null;
      let totalListed = 0;
      let totalProceeds = 0;

      for (const lot of lots) {
        if (remaining <= 0) break;
        const fromLot = Math.min(remaining, Number(lot.shares) || 0);
        if (fromLot <= 0) continue;

        const res = await apiClient.sellShares(lot.id, fromLot);
        lastData = res.data || {};
        remaining -= fromLot;
        totalListed += fromLot;
        if (typeof lastData.proceeds === "number") totalProceeds += lastData.proceeds;
      }

      if (remaining > 0) {
        throw new Error("Not enough shares available across your purchase lots.");
      }

      const inv = lots[0];
      const data = lastData || {};
      const propertyId = data.property?.propertyId || data.property?.id || inv.propertyId;
      if (data.property && data.property.sharesSold != null) {
        patchPropertyFundingState(propertyId, data.property.sharesSold, data.property.status);
      } else if (selectedProperty && data.mode !== "marketplace_listed") {
        patchPropertyFundingState(
          propertyId,
          Math.max(0, (selectedProperty.sharesSold || 0) - totalListed),
          "funding"
        );
      }
      if (data.mode === "marketplace_listed") {
        addToast(data.message || `Listed ${totalListed} share(s) on the marketplace.`, "success");
        loadMarketplaceListings();
      } else {
        if (typeof data.balance === "number") setBalance(data.balance);
        else if (totalProceeds > 0) setBalance((b) => Math.round((b + totalProceeds) * 100) / 100);
        addToast(
          data.message ||
            `Returned ${totalListed} share(s) to the offering. ${formatUsd(totalProceeds)} credited to your wallet.`,
          "success"
        );
        loadPropertyCatalog();
        loadProperties();
      }
      await refreshInvestments();
      apiClient.getWallet().then((w) => {
        if (Array.isArray(w.data?.transactions)) setWalletTransactions(w.data.transactions);
        if (typeof w.data?.balance === "number") setBalance(w.data.balance);
      }).catch(() => {});
    } catch (error) {
      addToast(error.message, "error");
    }
    setModal(null);
    setSelectedInvestment(null);
  };

  const buyMarket = (listing) => {
    if (!kycDone) { setShowKYC(true); return; }
    if (!isLoggedIn) { setPage("login"); return; }
    setSelectedMarketListing(listing);
    setModal("marketBuy");
  };

  const confirmBuyMarket = async (sharesToBuy) => {
    const listing = selectedMarketListing;
    if (!listing) return;
    const fees = computeMarketplaceTradeFees(listing.pricePerShare, sharesToBuy);
    if (fees.buyerPaysUsd > balance + 0.001) {
      addToast(`Insufficient funds (need ${formatUsd(fees.buyerPaysUsd)} incl. 1% platform fee).`, "error");
      return;
    }
    setBuyingMarket(true);
    try {
      const res = await apiClient.buyMarketplaceListing(listing.id, sharesToBuy);
      if (typeof res.data?.balance === "number") setBalance(res.data.balance);
      setMarketListings((prev) => prev.flatMap((l) => {
        if (String(l.id) !== String(listing.id)) return [l];
        const remaining = Math.max(0, (Number(l.shares) || 0) - sharesToBuy);
        return remaining > 0 ? [{ ...l, shares: remaining }] : [];
      }));
      await refreshInvestments();
      loadMarketplaceListings();
      addToast(
        `Purchased ${sharesToBuy} share(s) from ${listing.seller || "seller"} for ${formatUsd(fees.buyerPaysUsd)} (incl. fee).`,
        "success"
      );
      apiClient.getWallet().then((w) => {
        if (Array.isArray(w.data?.transactions)) setWalletTransactions(w.data.transactions);
      }).catch(() => {});
      setModal(null);
      setSelectedMarketListing(null);
    } catch (error) {
      addToast(error.message, "error");
    } finally {
      setBuyingMarket(false);
    }
  };

  const loadFxRates = async () => {
    try {
      const res = await apiClient.getFxRates();
      if (res.data?.ratesByCurrency) {
        mergeLiveFxRates(res.data.ratesByCurrency, {
          updatedAt: res.data.updatedAt,
          source: res.data.source,
          disclaimer: res.data.disclaimer
        });
      }
    } catch (err) {
      console.warn("FX rates unavailable, using bundled fallbacks:", err.message);
    }
  };

  const deposit = async (amountUsd) => {
    if (!isLoggedIn) { setPage("login"); return; }
    if (!testDepositsEnabled) {
      addToast("Test deposits are disabled. Card payments are not enabled yet.", "error");
      return;
    }
    const amount = Math.round(Number(amountUsd) * 100) / 100;
    if (!Number.isFinite(amount) || amount <= 0) {
      addToast("Enter a valid USD amount.", "error");
      return;
    }
    setDepositing(true);
    try {
      const res = await apiClient.depositFunds(amount, "test");
      const next = res.data?.balance ?? balance + amount;
      setBalance(next);
      returnFromDeposit();
      setModal(null);
      addToast(`Added ${formatUsd(amount)}. Balance: ${formatUsd(next)}`, "success");
    } catch (error) {
      addToast(error.message || "Deposit failed", "error");
    } finally {
      setDepositing(false);
    }
  };

  const syncKycStatus = async () => {
    try {
      const res = await apiClient.getMyKYC();
      const status = res.data?.status;
      if (status === "approved") setKycDone(true);
      else if (status === "pending" || status === "rejected") setKycDone(false);
    } catch (_) { /* no record yet */ }
  };

  const completeKYC = async (payload) => {
    try {
      await apiClient.submitKYC(payload);
      setKycDone(false);
      setShowKYC(false);
      addToast("Verification submitted. You can invest after an admin approves your ID.", "success");
    } catch (error) {
      addToast(error.message, "error");
    }
  };

  const completeAuthenticatedSession = (profile, { successMessage, nextPage = "home" } = {}) => {
    const role = profile?.role || "investor";
    setServerRole(role);
    setUser({ ...profile, role });
    setIsLoggedIn(true);
    if (successMessage) showAuthToast(successMessage);
    setPage(nextPage);
  };

  const redirectToVerifyEmail = (profile) => {
    const role = profile?.role || "investor";
    setServerRole(role);
    setUser({ ...profile, role });
    setIsLoggedIn(true);
    setPage("verify-email");
  };

  const handleEmailVerified = async () => {
    try {
      const profile = await refreshUserProfile();
      if (profile) {
        showAuthToast("Email verified — welcome to CoOwn!");
        setPage("home");
      }
    } catch (_) {
      setPage("home");
    }
  };

  const handleGoogleAuthSuccess = async (userData) => {
    if (userData?.isActive === false) {
      await handleSuspendedAccount();
      return;
    }
    const { needsEmailVerification, isNewUser, ...profile } = userData || {};
    if (needsEmailVerification || userNeedsEmailVerification(firebase.auth().currentUser)) {
      redirectToVerifyEmail(profile);
      showAuthToast(
        isNewUser
          ? "Account created. Please confirm your email to continue."
          : "Please confirm your email to continue.",
        "success"
      );
      return;
    }
    const name = profile?.firstName ? `, ${profile.firstName}` : "";
    completeAuthenticatedSession(profile, {
      successMessage: `Signed in successfully${name}!`,
      nextPage: "home",
    });
  };

  // --- Login / register / logout / Google sign-in ---
  const handleSuspendedAccount = async (message) => {
    try {
      await firebase.auth().signOut();
    } catch (_) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUser(null);
    setServerRole(null);
    setInvestments([]);
    setFavorites([]);
    setWalletTransactions([]);
    setBalance(0);
    showAuthToast(
      message || 'Your account has been suspended. Contact support@joincoown.com if you believe this is an error.',
      'error'
    );
    setPage('login');
  };

  useEffect(() => {
    const onSuspended = (event) => {
      handleSuspendedAccount(event?.detail?.message);
    };
    const onEmailNotVerified = () => {
      if (firebase.auth().currentUser && userNeedsEmailVerification(firebase.auth().currentUser)) {
        setPage("verify-email");
      }
    };
    window.addEventListener('coown-account-suspended', onSuspended);
    window.addEventListener('coown-email-not-verified', onEmailNotVerified);
    return () => {
      window.removeEventListener('coown-account-suspended', onSuspended);
      window.removeEventListener('coown-email-not-verified', onEmailNotVerified);
    };
  }, []);

  useEffect(() => {
    if (!authReady || !isLoggedIn || page === "verify-email") return;
    const currentUser = firebase.auth().currentUser;
    if (currentUser && userNeedsEmailVerification(currentUser) && serverRole !== "admin") {
      setPage("verify-email");
    }
  }, [authReady, isLoggedIn, page, serverRole]);

  const handleLogin = async (credentials) => {
    try {
      console.log('[LOGIN] Attempting Firebase email/password sign-in...');

      // Use Firebase Auth directly for email/password
      const { user, idToken } = await firebaseEmailLogin(credentials.email, credentials.password);
      console.log('[LOGIN] Firebase auth success, UID:', user.uid);

      localStorage.setItem('token', idToken);

      // Call backend to get user profile (backend verifies the ID token)
      const res = await apiClient.getMe();
      console.log('[LOGIN] Got user profile:', res.data);

      if (res.data?.isActive === false) {
        await handleSuspendedAccount();
        return;
      }

      if (userNeedsEmailVerification(user)) {
        redirectToVerifyEmail(res.data);
        showAuthToast('Please confirm your email before continuing.', 'success');
        return;
      }

      completeAuthenticatedSession(res.data, {
        successMessage: `Signed in successfully, ${res.data.firstName || 'Investor'}!`,
        nextPage: "home",
      });
    } catch (error) {
      console.error('[LOGIN] Failed:', error);
      if (error.code === 'auth/user-disabled' || String(error.message || '').includes('suspended')) {
        await handleSuspendedAccount(error.message);
        return;
      }
      showAuthToast(error.message || 'Login failed. Please check your credentials.', 'error');
    }
  };

  const handleRegister = async (formData) => {
    try {
      console.log('[REGISTER] Creating Firebase user...');

      // Create user in Firebase Auth first
      const displayName = `${formData.firstName} ${formData.lastName}`;
      const { user, idToken } = await firebaseEmailRegister(formData.email, formData.password, displayName);
      console.log('[REGISTER] Firebase user created, UID:', user.uid);

      // Register in backend with the ID token
      const res = await apiClient.register({
        ...formData,
        uid: user.uid
      });
      console.log('[REGISTER] Backend registration complete');

      localStorage.setItem('token', idToken);
      redirectToVerifyEmail(res.data.user);
      showAuthToast(`Account created. Check your email to confirm ${res.data.user.firstName || 'your account'}.`, 'success');
    } catch (error) {
      console.error('[REGISTER] Failed:', error);
      showAuthToast(error.message || 'Registration failed. Please try again.', 'error');
    }
  };

  const handleLogout = () => {
    // Sign out from Firebase Auth
    firebase.auth().signOut().then(() => {
      console.log('[LOGOUT] Firebase sign-out complete');
    }).catch((error) => {
      console.error('[LOGOUT] Firebase sign-out error:', error);
    });

    setIsLoggedIn(false);
    setUser(null);
    setServerRole(null);
    setInvestments([]);
    setFavorites([]);
    setWalletTransactions([]);
    setBalance(0);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setPage("home");
    showAuthToast("Signed out successfully.");
  };

  const refreshAgentSubmissions = async () => {
    if (!user?.email) return [];
    const res = await apiClient.getMySubmissions();
    if (res.data && Array.isArray(res.data)) {
      setAgentSubmissions(res.data);
      return res.data;
    }
    return [];
  };

  useEffect(() => {
    if (page === "agent-dashboard" && isLoggedIn && authReady) {
      refreshAgentSubmissions();
    }
  }, [page, isLoggedIn, authReady, user?.email]);

  const handleAgentSubmit = async (formData) => {
    const price = parseFloat(formData.price) || 0;
    const listingMode = formData.listingMode === "rent" ? "rent" : "sale";
    const payload = {
      ...formData,
      price,
      listingMode,
      images: formData.images || [],
      image: formData.images?.[0] || ""
    };
    const result = await apiClient.createSubmission(payload);
    const saved = result?.data;
    const newSub = {
      id: saved?.id || Date.now(),
      ...formData,
      ...saved,
      price,
      images: saved?.images || formData.images || [],
      image: saved?.image || formData.images?.[0] || "",
      submittedDate: saved?.submittedDate || new Date().toISOString().split("T")[0],
      status: saved?.status || "listed",
      pipelineDate: saved?.pipelineDate || new Date().toISOString().split("T")[0],
      notes: saved?.notes || "Awaiting admin review"
    };
    setAgentSubmissions((prev) => [newSub, ...prev.filter((s) => String(s.id) !== String(newSub.id))]);
    try {
      await refreshAgentSubmissions();
    } catch (_) { /* keep local row */ }
    addToast("Property submitted! Track progress under My Listings in the menu.");
    return saved || newSub;
  };

  const visibleFavorites = isLoggedIn ? favorites : [];
  const visibleInvestments = isLoggedIn ? investments : [];

  // --- Render: navbar + one page at a time (see page === "home" etc.) ---
  const showNavAndFooter = !["login", "register", "verify-email", "admin"].includes(page);

  return (
    <>
      {showNavAndFooter && (
        <Navbar
          page={page}
          setPage={navigateToPage}
          balance={balance}
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          country={country}
          onAddFunds={openDepositPage}
          favoritesCount={visibleFavorites.length}
          messagesUnreadCount={messagesUnreadCount}
        />
      )}

      {!kycDone && isLoggedIn && showNavAndFooter && page !== "how-it-works" && page !== "rent" && page !== "view-listings" && (
        <div className="container" style={{paddingTop:"1rem",paddingBottom:0}}>
          <div style={{background:"linear-gradient(90deg, var(--warning), #ffdb4d)",color:"#333",padding:"1rem 2rem",borderRadius:"12px",marginBottom:"1rem",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:"0.75rem"}}>
            <div><i className="fas fa-exclamation-triangle"></i> <strong>Complete identity verification</strong> to invest. Admin review is required after you submit your ID.</div>
            <button style={{background:"var(--text)",color:"white",border:"none",padding:"0.5rem 1.2rem",borderRadius:"8px",cursor:"pointer",fontWeight:600}} onClick={()=>setShowKYC(true)}>Verify Now</button>
          </div>
        </div>
      )}

      {page === "home" && !selectedProperty && (
        <HomePage properties={properties} favorites={visibleFavorites} compareList={compareList}
          onToggleFav={toggleFav} onToggleCompare={toggleCompare} onSelectProperty={selectProperty}
          onContactAgent={openListingChatForProperty}
          country={country} onCountryChange={setCountry}
          onViewListings={(opts) => goToBrowseListings(opts || { listingMode: "all" })} loading={loading}
          setPage={navigateToPage} isLoggedIn={isLoggedIn} />
      )}

      {page === "view-listings" && !selectedProperty && (
        <ViewListingsPage
          key="browse-default"
          properties={properties}
          catalogProperties={catalogProperties}
          favorites={visibleFavorites}
          compareList={compareList}
          onToggleFav={toggleFav}
          onToggleCompare={toggleCompare}
          onSelectProperty={selectProperty}
          onContactAgent={openListingChatForProperty}
          country={country}
          onCountryChange={setCountry}
          loading={loading}
          browsePreset="default"
        />
      )}

      {page === "funded-properties" && !selectedProperty && (
        <FundedPropertiesPage
          properties={properties}
          catalogProperties={catalogProperties}
          favorites={visibleFavorites}
          compareList={compareList}
          onToggleFav={toggleFav}
          onToggleCompare={toggleCompare}
          onSelectProperty={selectProperty}
          country={country}
          setPage={navigateToPage}
        />
      )}

      {page === "rent" && !selectedProperty && (
        <ViewListingsPage
          key="browse-rent"
          properties={properties}
          catalogProperties={catalogProperties}
          favorites={visibleFavorites}
          compareList={compareList}
          onToggleFav={toggleFav}
          onToggleCompare={toggleCompare}
          onSelectProperty={selectProperty}
          onContactAgent={openListingChatForProperty}
          country={country}
          onCountryChange={setCountry}
          loading={loading}
          browsePreset="rent"
        />
      )}

      {page === "favorites" && !selectedProperty && !isLoggedIn ? (
        <div className="container" style={{ paddingTop: "2rem", paddingBottom: "3rem" }}>
          <div style={{ textAlign: "center", padding: "4rem 1.5rem", background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
            <i className="far fa-heart" style={{ fontSize: "3rem", color: "var(--text-light)", marginBottom: "1rem", display: "block" }}></i>
            <h2>Sign in to view your favorites</h2>
            <p style={{ color: "var(--text-light)", margin: "1rem 0 1.5rem" }}>
              Saved properties are linked to your account and only appear while you are signed in.
            </p>
            <button type="button" className="btn btn-primary" onClick={() => navigateToPage("login")}>Log in</button>
          </div>
        </div>
      ) : null}
      {page === "favorites" && !selectedProperty && isLoggedIn && (
        <FavoritesPage
          properties={catalogProperties}
          favorites={visibleFavorites}
          compareList={compareList}
          onToggleFav={toggleFav}
          onToggleCompare={toggleCompare}
          onSelectProperty={selectProperty}
          country={country}
          isLoggedIn={isLoggedIn}
          setPage={navigateToPage}
        />
      )}

      {selectedProperty && (page === "home" || page === "rent" || page === "view-listings" || page === "funded-properties" || page === "marketplace" || page === "favorites") && (
        <PropertyDetail
          property={selectedProperty}
          onBack={clearSelectedProperty}
          onInvest={() => invest(selectedProperty)}
          userBalance={balance}
          investments={investments}
          country={country}
          isFavorite={isPropertyFavorited(visibleFavorites, selectedProperty.id)}
          onToggleFav={toggleFav}
          onContactAgent={openListingChatForProperty}
          isLoggedIn={isLoggedIn}
          addToast={addToast}
          onSignIn={() => navigateToPage("login")}
        />
      )}

      {listingChat?.conversation && (
        <ListingChatModal
          chat={listingChat}
          currentUid={firebase.auth().currentUser?.uid || user?.uid}
          sending={listingChatSending}
          onClose={closeListingChat}
          onSend={sendListingChatMessage}
          addToast={addToast}
          onOpenListing={openPropertyListing}
        />
      )}

      {page === "profile" && (
        <ProfilePage
          user={user}
          balance={balance}
          country={country}
          kycDone={kycDone}
          isLoggedIn={isLoggedIn}
          onOpenKYC={() => setShowKYC(true)}
          onAddFunds={openDepositPage}
          setPage={navigateToPage}
          onProfileSaved={refreshUserProfile}
          addToast={addToast}
        />
      )}

      {page === "dashboard" && (
        <DashboardPage
          user={user}
          isLoggedIn={isLoggedIn}
          investments={visibleInvestments}
          properties={catalogProperties.length ? catalogProperties : properties}
          onSell={sell}
          onViewProperty={openPropertyListing}
          country={country}
          kycDone={kycDone}
          onOpenKYC={() => setShowKYC(true)}
          setPage={navigateToPage}
          onAddFunds={openDepositPage}
          addToast={addToast}
          walletTransactions={walletTransactions}
          stripePublishableKey={stripePublishableKey}
        />
      )}

      {page === "marketplace" && !selectedProperty && (
        <MarketplacePage listings={marketListings} catalogProperties={catalogProperties}
          balance={balance} onBuy={buyMarket} onSelectProperty={selectProperty} country={country} />
      )}

      {page === "calculator" && <CalculatorPage country={country} />}
      {page === "news" && <NewsPage />}
      {page === "how-it-works" && <HowItWorksPage />}
      {page === "deposit" && (
        <DepositPage
          isLoggedIn={isLoggedIn}
          balance={balance}
          country={country}
          onBack={returnFromDeposit}
          setPage={navigateToPage}
          onConfirm={deposit}
          onStripeDeposit={async ({ success, error, amount }) => {
            if (error) {
              addToast(error, "error");
              return;
            }
            if (success) {
              addToast(`Card payment of ${formatUsd(amount)} received. Wallet updates when Stripe confirms.`);
              try {
                const w = await apiClient.getWallet();
                if (typeof w.data?.balance === "number") setBalance(w.data.balance);
                if (Array.isArray(w.data?.transactions)) setWalletTransactions(w.data.transactions);
              } catch (_) {}
              returnFromDeposit();
              setModal(null);
            }
          }}
          testDepositsEnabled={testDepositsEnabled}
          stripeEnabled={Boolean(stripePublishableKey)}
          stripePublishableKey={stripePublishableKey}
          depositing={depositing}
          addToast={addToast}
        />
      )}
      {page === "login" && <LoginPage onLogin={handleLogin} onGoogleSuccess={handleGoogleAuthSuccess} onSwitch={()=>setPage("register")} country={country} />}
      {page === "verify-email" && (
        <VerifyEmailPage
          email={user?.email || firebase.auth().currentUser?.email || ''}
          onVerified={handleEmailVerified}
          onSignOut={handleLogout}
        />
      )}
      {page === "register" && (
        <RegisterPage
          onRegister={handleRegister}
          onGoogleSuccess={handleGoogleAuthSuccess}
          onSwitch={() => setPage("login")}
          country={country}
          onOpenTerms={() => setPage("terms")}
          onOpenPrivacy={() => setPage("privacy-policy")}
        />
      )}
      {page === "list-property" && <ListPropertyPage onSubmit={handleAgentSubmit} user={user} addToast={addToast} setPage={navigateToPage} />}
      {page === "messages" && (
        <MessagesPage
          user={user}
          currentUid={firebase.auth().currentUser?.uid || user?.uid}
          isLoggedIn={isLoggedIn}
          setPage={navigateToPage}
          addToast={addToast}
          onUnreadChange={syncMessagesUnreadCount}
          onNotifyNewMessages={notifyNewMessages}
          onActiveConversationChange={handleActiveInboxConversationChange}
          initialConversationId={listingChat?.conversation?.id || null}
          onOpenListing={openPropertyListing}
        />
      )}

      {page === "agent-dashboard" && (
        <AgentDashboardPage
          submissions={agentSubmissions}
          properties={catalogProperties.length ? catalogProperties : properties}
          isLoggedIn={isLoggedIn}
          user={user}
          currentUid={firebase.auth().currentUser?.uid || user?.uid}
          onRefresh={refreshAgentSubmissions}
          addToast={addToast}
          onUnreadChange={syncMessagesUnreadCount}
          onNotifyNewMessages={notifyNewMessages}
          onActiveConversationChange={handleActiveInboxConversationChange}
          setPage={navigateToPage}
          onOpenListing={openPropertyListing}
        />
      )}
      {page === "admin" && (
        <AdminDashboardPage
          properties={properties}
          catalogProperties={catalogProperties}
          investments={investments}
          isAdmin={isAdmin}
          isLoggedIn={isLoggedIn}
          adminCheckPending={adminCheckPending}
          userEmail={user?.email}
          onPropertyCreated={async () => {
            await loadProperties();
            await loadPropertyCatalog();
          }}
          onGoHome={() => navigateToPage("home")}
          onGoLogin={() => navigateToPage("login")}
          addToast={showAuthToast}
        />
      )}

      {modal === "invest" && selectedProperty && (
        <InvestModal
          property={selectedProperty}
          balance={balance}
          existingShares={getSharesOwnedForProperty(investments, selectedProperty.id)}
          onClose={() => setModal(null)}
          onConfirm={confirmInvest}
          country={country}
        />
      )}

      {modal === "sell" && selectedProperty && selectedInvestment && (
        <SellModal
          investment={selectedInvestment}
          property={selectedProperty}
          onClose={() => { setModal(null); setSelectedInvestment(null); }}
          onConfirm={confirmSell}
          country={country}
        />
      )}

      {modal === "marketBuy" && selectedMarketListing && (
        <MarketplaceBuyModal
          listing={selectedMarketListing}
          property={findPropertyById(catalogProperties.length ? catalogProperties : properties, selectedMarketListing.propertyId)}
          balance={balance}
          buying={buyingMarket}
          onClose={() => { if (!buyingMarket) { setModal(null); setSelectedMarketListing(null); } }}
          onConfirm={confirmBuyMarket}
          country={country}
        />
      )}

      {showKYC && <KYCModal onClose={()=>setShowKYC(false)} onConfirm={completeKYC} country={country} />}

      {page === "terms" && <TermsPage setPage={navigateToPage} />}
      {LEGAL_PAGE_KEYS.filter((key) => key !== "terms").includes(page) && (
        <LegalPage pageKey={page} setPage={navigateToPage} />
      )}

      {page === "home" && !selectedProperty && <HomeAppSection />}
      {showNavAndFooter && <Footer setPage={navigateToPage} />}

      <CompareTray
        compareList={compareList}
        onRemove={removeFromCompare}
        onClear={clearCompare}
        onCompareTrigger={() => setCompareOpen(true)}
      />

      {compareOpen && (
        <CompareModal
          list={compareList}
          onClose={() => setCompareOpen(false)}
          onSelect={(prop) => selectProperty(prop)}
          country={country}
        />
      )}

      <div className="toast-container">
        {toasts.map(t => (
          <Toast key={t.id} message={t.msg} type={t.type} 
            onClose={()=>setToasts(prev=>prev.filter(x=>x.id!==t.id))} />
        ))}
      </div>
    </>
  );
};

export default App;
